import Vue from 'vue'
import 'isomorphic-unfetch'
import { ApolloClient } from 'apollo-client'
import { HttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloLink, Observable } from 'apollo-link'
import VueApollo from 'vue-apollo'
import store from '../store'
import router from '../router'
import { REFRESH_ACCESS_TOKEN_MUTATION } from '../constants/graphql/authentication'

const httpLink = new HttpLink({
  uri: process.env.VUE_APP_API_ENDPOINT + '/graphql',
  fetchOptions: {
    mode: 'cors',
 }
})

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const accessToken = store.getters.accessToken
  operation.setContext({
    headers: {
      Authorization: accessToken ? `${accessToken}` : null
    }
  })

  return forward(operation)
})

const versionCheckAfterware = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    const context = operation.getContext()
    const { response: { headers } } = context

    if (headers) {
      const version = headers.get('Version')

      if (store.getters.appVersion) {
        if (version && version != store.getters.appVersion && !store.getters.newVersionAvailable) {
          // update newVersionAvailable value if there is a version mismatch
          store.dispatch('updateNewVersionAvailable', true)
        }
      } else {
        // if version not set in vuex, then set version
        if (version) { store.dispatch('updateVersion', version) }
      }
    }

    return response
  })
})

// Error Handling
const errorLink = onError(({ response, graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      // if logged out use refresh token to get new access token and rerun query
      if (err.message == 'LOGGED_OUT') {
        return new Observable(observer => {
          const oldHeaders = operation.getContext().headers
          const userId = store.getters.userIdFromToken
          const refreshToken = store.getters.refreshToken

          refreshAccessToken(refreshToken, userId).then(refreshResponse => {
            const accessToken = refreshResponse.data.refreshAccessToken.accessToken
            const newRefreshToken = refreshResponse.data.refreshAccessToken.refreshToken

            store.dispatch('login', { accessToken, refreshToken: newRefreshToken })

            operation.setContext({
              headers: {
                ...oldHeaders,
                Authorization: accessToken,
              },
            })

            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer)
            }

            // Retry last failed request
            forward(operation).subscribe(subscriber)
          }).catch(error => {
            // Refresh unsuccessful, we force user to login
            observer.error(error)
            store.dispatch('logout')
            router.push({ name: 'Login', query: { error: 2 } })
            response.errors = null
          })
        })
      }

      // reload page if there is an error and a version mismatch
      const context = operation.getContext()
      const { response: { headers } } = context

      if (headers) {
        const version = headers.get('Version')
      
        if (store.getters.appVersion && version && version != store.getters.appVersion) {
          response.errors = null
          window.location.search += '&reload=true'
        }
      }
    }
  }
      
  if (networkError) console.log(`[Network error]: ${networkError}`)
})

function refreshAccessToken(refreshToken, userId) {
  return apolloClient.mutate({
    mutation: REFRESH_ACCESS_TOKEN_MUTATION,
    variables: {
      refreshToken,
      userId
    }
  })
}

var link = authMiddleware.concat(httpLink)
link = versionCheckAfterware.concat(link)

export const apolloClient = new ApolloClient({
  fetchOptions: { fetch },
  link: errorLink.concat(link),
  cache: new InMemoryCache(),
  connectToDevTools: true,
  fetchOptions: {
    mode: 'no-cors',
  },
})

Vue.use(VueApollo)

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $loadingKey: 'loading',
  },
})